import React, { useEffect, useState } from 'react';
import { Text, Group, Stack, Paper, Button, Container } from '@mantine/core';
import { IconSettings } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { MemberList } from '@/pages/NewAdminConsole/components/UnassignedUsersManagement/MemberList';
import { usePermissionControl } from '@/pages/NewAdminConsole/hooks/usePermissionControl';
import { User } from '@/pages/NewAdminConsole/services/permissionApi';
import { useAssignUserStore } from '@/pages/NewAdminConsole/components/modals/AssigningUsersModal/store';
import { usePermissionData } from '@/pages/NewAdminConsole/hooks/usePermissionData';
import { AssigningUsersModal } from '@/pages/NewAdminConsole/components/modals/AssigningUsersModal';
import { MEMBER_LIST_PAGE_SIZE } from '@/pages/NewAdminConsole/components/MemberManagement';

interface UnassignedUsersProps {
  users: User[];
}

export const UnassignedUsersManagement: React.FC<UnassignedUsersProps> = ({ users }) => {
  const { t } = useTranslation();
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const permissionControl = usePermissionControl();
  const { loadMembersData } = usePermissionData();
  const [activePage, setActivePage] = useState(1);
  const filterWithPagination = users.slice(
    (activePage - 1) * MEMBER_LIST_PAGE_SIZE,
    activePage * MEMBER_LIST_PAGE_SIZE
  );
  const totalPage = Math.ceil(users.length / MEMBER_LIST_PAGE_SIZE);

  const onPageChange = (page: number) => {
    setActivePage(page);
    setSelectedMembers([]);
  };

  // 分配用户对话框 store
  const openAssignDialog = useAssignUserStore((state) => state.openDialog);

  // 处理分配用户
  const onAssignUser = (users: User[]) => {
    openAssignDialog(users, async () => {
      setSelectedMembers([]);
      // 分配成功后的回调（可选）
      await loadMembersData(true);
    });
  };

  // 全选/取消全选
  const toggleAllMembers = () => {
    // 判断的对象只能是自己有权限（删除）的用户
    // 先将这部分用户过滤出来
    const filteredMembersHavingPermission = filterWithPagination.filter(permissionControl.canDeleteUser);

    if (selectedMembers.length === filteredMembersHavingPermission.length) {
      setSelectedMembers([]);
    } else {
      setSelectedMembers(filteredMembersHavingPermission.map((m) => m.id.toString()));
    }
  };

  // 切换成员选择
  const toggleMemberSelection = (memberId: number | string) => {
    const id = typeof memberId === 'string' ? memberId : memberId.toString();
    setSelectedMembers((prev) => (prev.includes(id) ? prev.filter((prevId) => prevId !== id) : [...prev, id]));
  };

  useEffect(() => {
    // 用户数据变化时，重置页码到第一页
    onPageChange(1);
  }, [users]);

  if (filterWithPagination.length === 0) {
    return (
      <Paper p="xl" radius="md" style={{ textAlign: 'center' }}>
        <Text size="lg" fw={500} mb="xs">
          {t('adminConsole.newConsole.unassignedUsers.noData')}
        </Text>
        <Text size="sm" c="dimmed">
          {t('adminConsole.newConsole.unassignedUsers.allAssigned')}
        </Text>
      </Paper>
    );
  }

  return (
    <Container fluid p={0} h="100vh" style={{ overflow: 'hidden' }}>
      <Stack>
        <Group justify="space-between">
          <Text size="sm" c="dimmed" mt="xs">
            {t('adminConsole.newConsole.unassignedUsers.description')}
          </Text>

          <Group>
            <Button
              size="sm"
              leftSection={<IconSettings size={14} />}
              className="filled"
              disabled={selectedMembers.length === 0}
              onClick={() => onAssignUser(filterWithPagination.filter((user) => selectedMembers.includes(user.id)))}
            >
              {t('adminConsole.newConsole.unassignedUsers.assignPermission')}
            </Button>
          </Group>
        </Group>

        <MemberList
          activePage={activePage}
          totalPage={totalPage}
          members={filterWithPagination}
          selectedMembers={selectedMembers}
          onToggleAll={toggleAllMembers}
          onToggleSelection={toggleMemberSelection}
          onAssignUser={onAssignUser}
          onPageChange={onPageChange}
        />
      </Stack>
      {/* 分配用户对话框 */}
      <AssigningUsersModal />
    </Container>
  );
};
